import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * ① 레일 라벨 칩 — 정본 `axi-components.html` **H1-3**.
 *
 * 접힌 레일(아이콘 전용)에서 마우스를 올린 항목의 **이름**을 상자 바깥에 띄운다.
 * 펼친 상태에서는 이름이 이미 보이므로 **띄우지 않는다**(호출부가 켜고 끈다).
 *
 * ⚠ **반드시 body 로 portal 한다.** `.navbar` 는 접기 애니메이션 때문에 `overflow:hidden`
 *   이라, 레일 안에서 그리면 칩이 통째로 잘린다(정본 F4 가 같은 경고를 남겼고 채팅 첨부
 *   메뉴가 실제로 이걸로 잘렸었다).
 *
 * 지연 — 정본 F5 Tooltip 의 400ms 는 레일에 느리다. 레일은 **읽는 곳이 아니라 가는 곳**이라
 *   150ms 에 열고, 레일 안에서 항목을 훑는 동안(직전 닫힘 400ms 이내)에는 **기다리지 않는다**.
 *
 * 배지는 이름 뒤에 함께 적는다 — 접힘에서는 아이콘 위의 숫자만 보여 **무엇의 7인지** 모른다.
 *
 * 접근성: 칩은 **마우스·키보드 포커스용 표시**이고 `aria-hidden` 이다. 보조기기가 읽는 것은
 *   항목 자신의 `aria-label` 이라 둘은 별개다(F5 규칙 그대로).
 */

/** 여는 지연(ms) — 처음 한 번. */
const OPEN_DELAY = 150;
/** 이어 여는 지연이 0 이 되는 시간창(ms) — 이 안에 다시 들어오면 즉시 뜬다. */
const GROUP_WINDOW = 400;
/** 닫는 지연(ms). */
const CLOSE_DELAY = 80;
/** 상자와 칩 사이(px). */
const GAP = 8;
/** 칩 높이(px) — 세로 중앙 정렬 계산에 쓴다. */
const CHIP_H = 26;

export interface RailChipTarget {
  el: HTMLElement;
  label: string;
  badge?: number;
  /** 우측 레일이면 칩이 **왼쪽**에 뜬다. */
  side?: 'left' | 'right';
}

interface Placed extends RailChipTarget {
  top: number;
  left: number | null;
  right: number | null;
}

/**
 * 레일 라벨 칩 하나를 띄운다. 호출부는 `target` 만 넘기고(없으면 `null`) 지연·자리·portal 은
 * 여기서 맡는다. `enabled` 가 거짓이면 아무것도 그리지 않는다(펼친 레일).
 */
export function RailLabelChip({ target, enabled }: { target: RailChipTarget | null; enabled: boolean }) {
  const [placed, setPlaced] = useState<Placed | null>(null);
  const lastHideRef = useRef(0);
  const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (openTimer.current) clearTimeout(openTimer.current);
    if (closeTimer.current) clearTimeout(closeTimer.current);

    if (!enabled || !target) {
      closeTimer.current = setTimeout(() => {
        setPlaced((prev) => {
          if (prev) lastHideRef.current = Date.now();
          return null;
        });
      }, CLOSE_DELAY);
      // ★ 2026-09-14 — 언마운트 뒤 닫기 타이머가 발화하지 않게 치운다(화면 동작 동일 — 다시 돌 때는 위 두 줄이 이미 치운다).
      //   치우지 않으면 검사에서 jsdom 을 내린 뒤 setPlaced 가 불려 `window is not defined` unhandled 가 났다(#1277).
      return () => {
        if (closeTimer.current) clearTimeout(closeTimer.current);
      };
    }

    const wait = Date.now() - lastHideRef.current < GROUP_WINDOW ? 0 : OPEN_DELAY;
    openTimer.current = setTimeout(() => {
      const r = target.el.getBoundingClientRect();
      const onRight = target.side !== 'left';
      setPlaced({
        ...target,
        top: Math.round(r.top + r.height / 2 - CHIP_H / 2),
        left: onRight ? Math.round(r.right + GAP) : null,
        right: onRight ? null : Math.round(window.innerWidth - r.left + GAP),
      });
    }, wait);

    return () => {
      if (openTimer.current) clearTimeout(openTimer.current);
    };
  }, [target, enabled]);

  /** 스크롤·창 크기 변화 중에는 자리가 어긋나므로 닫는다(다시 올리면 뜬다). */
  useEffect(() => {
    if (!placed) return;
    const drop = () => setPlaced(null);
    window.addEventListener('scroll', drop, true);
    window.addEventListener('resize', drop);
    return () => {
      window.removeEventListener('scroll', drop, true);
      window.removeEventListener('resize', drop);
    };
  }, [placed]);

  if (!placed) return null;

  return createPortal(
    <span
      className="rail-chip"
      aria-hidden
      style={{
        top: placed.top,
        ...(placed.left !== null ? { left: placed.left } : { right: placed.right ?? 0 }),
      }}
    >
      {placed.label}
      {placed.badge !== undefined && placed.badge > 0 && (
        <b className="rail-chip__badge">{placed.badge > 99 ? '99+' : placed.badge}</b>
      )}
    </span>,
    document.body,
  );
}
